import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Typography, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import FeedbackIcon from '@material-ui/icons/Feedback';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

// CONSTANTS
import { hCaptchaKey } from '@/constants/common';
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL } from '@/constants/routes';
import { LOCATIONS_SUBSCIPTION_FORM_PAGE } from '@/constants/locations';
import { SubscriptionFlow } from '@/constants/subscription';

// UTILITY
import { getLink, routeEnhance } from '@/utility/routes';
import store from '@/utility/store';
import {
  analyticsInternalLinks,
  analyticsStartedSubscriptionCheckout,
  analyticsSubscriptionSpecOffer,
} from '@/utility/analytics';
import { getSearchParams } from '@/utility/common';
import bugsnagClient from '@/utility/bugsnagClient';

// COMPONENTS
import Redirect from '@/components/Redirect';

// HELP
import SubscriptionPlanInfo from './help/SubscriptionPlanInfo';
import SubscriptionWizard from './help/SubscriptionWizard';
import SubscriptionStepTemplate from './help/SubscriptionStepTemplate';
import SubscriptionBreadcrumbs from './help/SubscriptionBreadcrumbs';
import PaymentForm from './help/PaymentForm';
import SubscriptionPlanForm from './help/SubscriptionPlanForm';
import RegistrationSuccessPromocode from './help/RegistrationSuccessPromocode';
import RegistrationForm from './help/RegistrationForm';
import RegistrationSuccessTemplate from './help/RegistrationSuccessTemplate';
import AuthDialog from './help/AuthDialog';

// STYLES
import { SSubscriptionForm, SMainContainer } from './styles';

const alertIconMapping = {
  error: <FeedbackIcon />,
};

function getDefSpecialOffer() {
  const params = getSearchParams(document.location.search);
  const specOfferFromUrl = params.get('spec-offer');
  const storedSpecOffer = store.get('sub-spec-offer');

  return specOfferFromUrl || storedSpecOffer || 'no';
}

function SubscriptionForm({
  partner,
  user,
  dictionary,
  preview,
  addSubscriptionRegistration,
  manageAuthDialogState,
  cards,
  errors,
  loading,
  cardsLoading,
  paymentLoading,
  previewLoading,
}) {
  const {
    app: { name, unique_slug, id },
    subscription,
    selectedPlan,
  } = dictionary;
  const merchantType = subscription?.merchant?.slug;
  const isSlingType = merchantType === 'sling-tv' || merchantType === 'sling-latino';
  const isBangoType = merchantType === 'BANGO_US';
  const flow = subscription?.registration_meta?.flow;
  const isDefReg = flow === SubscriptionFlow.DEFERRED_REGISTRATION;
  const isPromocodeFlow = flow === SubscriptionFlow.SINGLE_CHARGE_PROMOCODE;

  const defSpecOffer = isSlingType ? getDefSpecialOffer() : 'no';

  const [activeStep, setActiveStep] = useState(0);
  const [captchaError, setCaptchaError] = useState(null);
  const [claimSpecialOffer, setClaimSpecialOffer] = useState(defSpecOffer);

  const navigate = useNavigate();
  const [t] = useTranslation();

  useEffect(() => {
    analyticsStartedSubscriptionCheckout({
      location: LOCATIONS_SUBSCIPTION_FORM_PAGE,
      id,
    });

    if (defSpecOffer === 'yes') {
      analyticsSubscriptionSpecOffer({
        location: LOCATIONS_SUBSCIPTION_FORM_PAGE,
        app_id: id,
        claim_special_offer: defSpecOffer,
      });
    }
  }, []);

  function getBreadcrumbs({
    app_account,
    active_subscription,
    subscription: {
      merchant,
      products,
      registration_meta: { payment },
    },
  }) {
    const type = merchant.slug;
    const isNotActivePlan =
      active_subscription &&
      selectedPlan &&
      Number.isInteger(active_subscription.subscription_plan_id)
        ? selectedPlan.id !== active_subscription.subscription_plan_id
        : false;

    const subscriptionPlanBreadcrumb = {
      type: 'subscriptionPlan',
      title: t('subscription.subscriptionPlan.breadcrumb'),
      onClick: () => setActiveStep(0),
    };

    const registrationBreadcrumb = {
      type: 'registration',
      title: t('subscription.registration.breadcrumb'),
    };

    const paymentStripeBreadcrumb = {
      type: 'paymentStripe',
      title: t('subscription.payment.breadcrumb'),
    };

    const vimeoRegSuccessBreadcrumb = {
      type: 'registrationSuccessVimeo',
      title: null,
    };

    const promocodeRegSuccessBreadcrumb = {
      type: 'registrationSuccessPromocode',
      title: t('subscription.promocode.breadcrumb'),
    };

    const isVimeoApp = type === 'vimeo';
    const hasProductsOrAddons =
      products.length && (products.length > 1 || products[0].addOns?.length);
    const displaySubscriptionPlanStep =
      (!active_subscription || isNotActivePlan) && hasProductsOrAddons;
    const displayPaymentStripeStep =
      payment && payment === 'stripe' && (!active_subscription || isNotActivePlan);
    const displayRegistrationStep = !app_account;
    const displayVimeoRegistrationSuccessStep = !app_account && isVimeoApp;

    const steps = [
      displaySubscriptionPlanStep && subscriptionPlanBreadcrumb,
      displayRegistrationStep && registrationBreadcrumb,
      displayPaymentStripeStep && paymentStripeBreadcrumb,
      displayVimeoRegistrationSuccessStep && vimeoRegSuccessBreadcrumb,
      isPromocodeFlow && promocodeRegSuccessBreadcrumb,
    ];

    return steps.filter(Boolean);
  }

  const breadcrumbs = getBreadcrumbs(dictionary);

  const breadcrumbsComponent = breadcrumbs.length && (
    <SubscriptionBreadcrumbs
      className="step-breadcrumbs"
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      breadcrumbs={breadcrumbs}
    />
  );

  function generateSuccessAlert() {
    const slug = subscription.merchant.slug;

    const message = t(`subscription.notifications.acc_created`, {
      app: name,
      context: slug,
    });

    store.set(`subscription-${unique_slug}`, {
      type: 'success',
      message,
      spec_offer: claimSpecialOffer,
    });
  }

  const stepTemplates = {
    subscriptionPlan: {
      render: ({ onNext }) => {
        return (
          <SubscriptionStepTemplate
            breadcrumbs={breadcrumbsComponent}
            title={t('subscription.subscriptionPlan.title')}
            form={
              <SubscriptionPlanForm
                onNext={onNext}
                dictionary={dictionary}
                analyticsLocation={LOCATIONS_SUBSCIPTION_FORM_PAGE}
              />
            }
          />
        );
      },
    },
    registration: {
      render: ({ onNext }) => {
        const accountFieldsLength = subscription?.registration_meta?.account_fields?.length || 0;
        const errorMessage = errors.registration || captchaError;
        const hasError = !!errorMessage;

        const errorAlert = hasError && (
          <Alert
            severity="error"
            iconMapping={alertIconMapping}
            className="primary submit-error-alert"
          >
            <AlertTitle>{t('subscription.notifications.error_default')}</AlertTitle>
            <Typography variant="body1">{errorMessage}</Typography>
          </Alert>
        );

        return (
          <SubscriptionStepTemplate
            breadcrumbs={breadcrumbsComponent}
            title={t('subscription.registration.title', {
              app: isBangoType || isDefReg ? 'MyBundle' : name,
            })}
            description={t('subscription.registration.subtitle', {
              count: accountFieldsLength,
              app: isBangoType || isDefReg ? 'MyBundle' : name,
            })}
            error={errorAlert}
            form={
              <RegistrationForm
                disabled={!!captchaError}
                user={user}
                loading={loading}
                onNext={onNext}
                partner={partner}
                dictionary={dictionary}
                addSubscriptionRegistration={addSubscriptionRegistration}
                analyticsLocation={LOCATIONS_SUBSCIPTION_FORM_PAGE}
                manageAuthDialogState={manageAuthDialogState}
              />
            }
          />
        );
      },
    },
    registrationSuccessVimeo: {
      render: ({ onNext }) => {
        const type = subscription.merchant.slug;
        const linkHref =
          dictionary.app_account_temp?.url || dictionary.app_account?.subscription_url;

        return (
          <SubscriptionStepTemplate
            breadcrumbs={breadcrumbsComponent}
            title={t('subscription.registration.title', { app: name })}
            description={t('subscription.registration.create_password_subtitle')}
            form={
              <RegistrationSuccessTemplate
                type={type}
                skipButton={
                  <Button color="secondary" variant="outlined" onClick={onNext}>
                    {t('buttons.goToMyApp')}
                  </Button>
                }
                linkButton={
                  <Button
                    color="primary"
                    component="a"
                    href={linkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('buttons.createPassword')}
                  </Button>
                }
                linkDescription={t('subscription.registration.create_password_desc', {
                  app: name,
                })}
              />
            }
          />
        );
      },
    },
    registrationSuccessPromocode: {
      render: ({ onNext }) => {
        return (
          <RegistrationSuccessPromocode
            breadcrumbs={breadcrumbsComponent}
            onNext={onNext}
            partner={partner}
            dictionary={dictionary}
            analyticsLocation={LOCATIONS_SUBSCIPTION_FORM_PAGE}
          />
        );
      },
    },
    paymentStripe: {
      render: ({ onNext }) => {
        function handleOnNext(...params) {
          if (!isPromocodeFlow) generateSuccessAlert();

          onNext(...params);
        }

        return (
          <PaymentForm
            breadcrumbs={breadcrumbsComponent}
            errors={{ ...errors, captcha: captchaError }}
            partner={partner}
            onNext={handleOnNext}
            dictionary={dictionary}
            loading={loading}
            cardsLoading={cardsLoading}
            paymentLoading={paymentLoading}
            previewLoading={previewLoading}
            user={user}
            analyticsLocation={LOCATIONS_SUBSCIPTION_FORM_PAGE}
            specOfferValue={claimSpecialOffer}
          />
        );
      },
    },
  };

  const steps = breadcrumbs.map(({ type }) => stepTemplates[type]).filter(Boolean);

  const routeEnhanced = routeEnhance(
    getLink(ROUTE_PROFILE_SUBSCRIPTION_DETAIL, {
      slug: unique_slug,
    }),
    {
      state: {
        location: LOCATIONS_SUBSCIPTION_FORM_PAGE,
      },
    },
  );

  if (!steps.length) {
    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: window.location.pathname,
      from_search_params: window.location.search,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    return <Redirect to={routeEnhanced} />;
  }

  function onLastNext() {
    store.remove('subscription');
    store.remove('sub-spec-offer');

    if (!isPromocodeFlow) generateSuccessAlert();

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: window.location.pathname,
      from_search_params: window.location.search,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    navigate(routeEnhanced, { replace: true });
  }

  function onCaptchaError(message) {
    bugsnagClient.notify(new Error(`Captcha isn't available in subsciprtion. ${message}`));

    setCaptchaError(t('notification.encProblem'));
  }

  function handleClickSpecOffer() {
    const newValue = claimSpecialOffer === 'no' ? 'yes' : 'no';

    setClaimSpecialOffer(newValue);

    store.set('sub-spec-offer', newValue);

    analyticsSubscriptionSpecOffer({
      location: LOCATIONS_SUBSCIPTION_FORM_PAGE,
      app_id: id,
      claim_special_offer: newValue,
    });
  }

  const totalSteps = steps.length;
  const isLastStep = activeStep === totalSteps - 1;
  const hasPlanStep = breadcrumbs.find(breadcrumb => breadcrumb.type === 'subscriptionPlan');
  const showEditPlan = !!hasPlanStep && activeStep !== 0;
  const showHCaptcha = isSlingType && !!hCaptchaKey;
  const hasPartner = Boolean(partner.slug);

  return (
    <SSubscriptionForm className={clsx(hasPartner && `partner-${partner.slug}`)}>
      <SMainContainer className={clsx(hasPartner && 'container--partner')}>
        <div className="main-info-wrapper">
          {showHCaptcha && (
            <HCaptcha
              sitekey={hCaptchaKey}
              size="invisible"
              onError={onCaptchaError}
              // onVerify is required
              onVerify={() => {}}
            />
          )}
          <AuthDialog disabled={!!captchaError} />
          <SubscriptionWizard
            steps={steps}
            activeStep={activeStep}
            onChange={setActiveStep}
            onLastNext={onLastNext}
          />
        </div>
        <div className="plan-info-wrapper">
          <SubscriptionPlanInfo
            dictionary={dictionary}
            cards={cards}
            showEditPlan={showEditPlan}
            onEditPlanClick={() => setActiveStep(0)}
            isLastStep={isLastStep}
            preview={preview}
            previewLoading={previewLoading}
            specOfferValue={claimSpecialOffer}
            onSpecOfferClick={handleClickSpecOffer}
          />
        </div>
      </SMainContainer>
    </SSubscriptionForm>
  );
}

SubscriptionForm.propTypes = {
  partner: PropTypes.object.isRequired,
  user: PropTypes.object,
  cards: PropTypes.array.isRequired,
  dictionary: PropTypes.object.isRequired,
  preview: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  cardsLoading: PropTypes.bool.isRequired,
  paymentLoading: PropTypes.bool.isRequired,
  previewLoading: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  addSubscriptionRegistration: PropTypes.func.isRequired,
  manageAuthDialogState: PropTypes.func.isRequired,
};

export default SubscriptionForm;
